import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Page from '@pelckmans/elementis/components/page';

import { getMedialinks, Medialink } from '../../api/medialink';
import { getModuleById, Module } from '../../api/module';
import { fetchTocNodes, NodeWithoutDescription } from '../../api/table-of-content';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import Footer from '../../layout/footer/Footer';
import ModuleAlerts from '../../shared-components/alerts/ModuleAlerts';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import { groupMaterialsByNode } from '../../utils/medialink';
import { flattenNodes } from '../../utils/tocNode';
import MaterialSection from './components/MaterialSection';
import ContentFooter from '../../layout/content/ContentFooter';
import { getSlideSetsBySearch, SlideSet } from '../../api/slide-set';

function LinkedMaterialList({ pastThreshold }: WithScrollThresholdProps) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  useDocumentTitle(t('PAGES.LINKED_MATERIAL_LIST.DOCUMENT_TITLE'));

  const moduleId = searchParams.get('moduleId') || '';
  const medialinkIds = searchParams.get('medialinkIds') || '';
  const slideSetIds = searchParams.get('slideSetIds') || '';

  const [medialinksByNodeId, setMedialinksByNodeId] = useState<Record<string, Medialink[]>>();
  const [slideSetsByNodeId, setSlideSetsByNodeId] = useState<Record<string, SlideSet[]>>();
  const [module, setModule] = useState<Module>();
  const [superModule, setSuperModule] = useState<Module>();
  const [loading, setLoading] = useState<boolean>(false);
  const [flattenedTocNodes, setFlattenedTocNodes] = useState<NodeWithoutDescription[]>();

  const stableRefetch = useCallback(async () => {
    const [{ data: medialinks }, { data: slideSets }] = await Promise.all([
      getMedialinks(moduleId, { ids: medialinkIds }),
      getSlideSetsBySearch(moduleId, { slideSetIds, includeNodeHierarchy: true }),
    ]);

    setMedialinksByNodeId(groupMaterialsByNode(medialinks));
    setSlideSetsByNodeId(groupMaterialsByNode(slideSets));
  }, [medialinkIds, moduleId, slideSetIds]);

  useEffect(() => {
    if (moduleId) return;
    handleError(new Error('Module not found'));
  }, [handleError, moduleId]);

  useEffect(() => {
    if ((!medialinkIds && !slideSetIds) || !moduleId) return;
    setLoading(true);

    async function fetchMaterials() {
      try {
        const [{ data: _medialinks }, _module, tocNodes, { data: _slideSets }] = await Promise.all([
          getMedialinks(moduleId, { ids: medialinkIds }),
          getModuleById(moduleId),
          fetchTocNodes(moduleId),
          getSlideSetsBySearch(moduleId, { slideSetIds, includeNodeHierarchy: true }),
        ]);

        if (_module.superModuleId) {
          const _superModule = await getModuleById(_module.superModuleId);
          setSuperModule(_superModule);
        } else {
          setSuperModule(_module);
        }

        setMedialinksByNodeId(groupMaterialsByNode(_medialinks));
        setSlideSetsByNodeId(groupMaterialsByNode(_slideSets));
        setModule(_module);
        setFlattenedTocNodes(flattenNodes(tocNodes.data));
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    }

    fetchMaterials();
  }, [handleError, medialinkIds, moduleId, slideSetIds]);

  if (!moduleId) {
    return null;
  }

  return (
    <div className={classNames('page', 'linked-material-list-page')}>
      <div className="page-content">
        <div className="main-layout">
          <div className="main-layout-container">
            <div className="content-layout">
              <main>
                <div className="margin-wrapper">
                  <ContentHeader
                    cover={
                      <Page.Header
                        density={pastThreshold ? 'compact' : 'normal'}
                        headerText={t('PAGES.LINKED_MATERIAL_LIST.PAGE_TITLE')}
                        breadcrumbs={
                          superModule ? (
                            <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                              <Breadcrumbs.Item
                                avatarImage={
                                  superModule.styling?.logo ? (
                                    <AvatarImage name={superModule.name} src={superModule.styling?.logo} />
                                  ) : undefined
                                }
                                to={`${location.pathname}${location.search}${location.hash}`}
                              >
                                {superModule.name}
                              </Breadcrumbs.Item>
                            </Breadcrumbs>
                          ) : undefined
                        }
                      />
                    }
                  />
                  <ContentBody alerts={module ? <ModuleAlerts module={module} /> : null}>
                    <div className="max-container overview-mode">
                      <div className="list-sections">
                        <MaterialSection
                          medialinksByNodeId={medialinksByNodeId}
                          slideSetsByNodeId={slideSetsByNodeId}
                          module={module}
                          superModule={superModule}
                          flattenedTocNodes={flattenedTocNodes}
                          loading={loading}
                          refetch={stableRefetch}
                        />
                      </div>
                    </div>
                  </ContentBody>
                  <ContentFooter>
                    <Footer />
                  </ContentFooter>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withScrollThreshold(LinkedMaterialList);
